import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronDownIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { DEFAULT_TCPA_DISCLOSURE } from '../data/tcpaDisclosure';
import { TCPADisclosure } from './TCPADisclosure';
import { getProgramMatch, type ProgramTemplate } from '../data/programTemplates';
import { clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
const startDateOptions = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 60; y--) years.push(String(y));
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
const STEP_LABELS = ['Program', 'Background', 'Location', 'Contact'];

/**
 * Minimum a picker option needs. `ProgramTemplate` satisfies this, and so do
 * the generated degree × program combinations, which have no template entry.
 */
export interface MatchFormProgram {
  slug: string;
  name: string;
  credential: string;
}
interface HeroMatchFormProps {
  /** Programs offered in the Step 0 picker. */
  programs: MatchFormProgram[];
  /** Pre-selected program, when the page is about one. */
  initialProgramSlug?: string;
  /** Show each option's domain, for the all-programs index. */
  domainNameFor?: (program: MatchFormProgram) => string | undefined;
  /**
   * Media Matchers codes for an option. Defaults to the program-template
   * lookup; generated combinations pass their own codes instead.
   */
  matchFor?: (program: MatchFormProgram) => {
    degreeInterest?: string;
    areaStudy?: string;
    mProgram?: string;
  };
  heading?: string;
  subtext?: string;
  /**
   * Fires whenever the visitor moves between steps, so the hosting page can
   * give the form more room once it reaches the contact step.
   */
  onStepChange?: (step: number) => void;
  /** Lays the contact fields out in two columns when the card is wide. */
  wide?: boolean;
}

/**
 * Lead form that lives directly in a program page hero, so a visitor can
 * complete the whole match without leaving the page they landed on.
 *
 * Step 0 picks the program, 1 collects background, 2 location, 3 contact. When
 * the page is about a single program, `initialProgramSlug` answers step 0 and
 * the picker is skipped entirely. Both Media Matchers Searches fire when step 2
 * completes — before the contact step renders — so the partner's own disclosure
 * is the text shown beside the consent checkbox.
 */
export function HeroMatchForm({
  programs,
  initialProgramSlug,
  domainNameFor,
  matchFor,
  heading = 'Get Matched Now',
  subtext = 'Accredited programs. No obligation.',
  onStepChange,
  wide = false
}: HeroMatchFormProps) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  // A page about one program has already answered the program question, so the
  // picker is skipped rather than asking the visitor to restate it.
  const hasFixedProgram = !!initialProgramSlug;
  const firstStep = hasFixedProgram ? 1 : 0;
  const [step, setStep] = useState(firstStep);
  const [programSlug, setProgramSlug] = useState(initialProgramSlug || '');
  const [programError, setProgramError] = useState('');
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();

  // Populates the LeadiD and TrustedForm hidden inputs before anything fires.
  useLeadTracking();
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const searchFired = useRef(false);
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);
  const selectedProgram = programs.find(p => p.slug === programSlug);
  const buildProfile = useCallback((): SearchProfile => {
    const resolveMatch = matchFor || ((p: MatchFormProgram) => getProgramMatch(p as ProgramTemplate));
    const context = selectedProgram ? resolveMatch(selectedProgram) : {};
    return {
      degreeInterest: context.degreeInterest || '',
      areaStudy: context.areaStudy || '',
      mProgram: context.mProgram || '',
      currentEducation: formData.currentEducation,
      gradYear: formData.gradYear,
      startTimeframe: formData.startDate,
      militaryAffiliation: formData.isMilitary,
      zipCode: formData.zipCode.replace(/\D/g, ''),
      address: formData.address,
      city: formData.city,
      state: formData.state
    };
  }, [selectedProgram, formData, matchFor]);

  /**
   * TCPA Search runs as soon as the ZIP is in, so the partner's own consent
   * language is on screen before the visitor ticks the box.
   */
  const runTcpaSearch = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const result = await tcpaSearch(buildProfile());
    setTcpa(result);
    if (!result) setTcpaFailed(true);
    setSearchLoading(false);
    return result;
  }, [buildProfile]);
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* Fall through to the manual city/state fields. */
    } finally {
      setZipLookedUp(true);
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!programSlug) {
      setProgramError('Please choose a program to continue.');
      return;
    }
    setStep(1);
  };
  const handleBackgroundNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateUniStep1()) setStep(2);
  };
  const handleLocationNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateUniStep2()) return;
    // Last field the TCPA payload needs, so its disclosure is in hand by the
    // time the consent checkbox renders.
    if (!searchFired.current) {
      searchFired.current = true;
      runTcpaSearch();
    }
    setStep(3);
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    if (!validateUniStep3()) return;
    if (!tcpa) {
      setTcpaFailed(true);
      setSubmitError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    await tcpaPost(tcpa.mRecordID, contact);
    // Leads Search runs here so it carries the complete profile, and excludes
    // the brand the TCPA call already matched.
    const [matchedLeads, clicks] = await Promise.all([leadsSearch(profile, contact, tcpa.mBrandID), clicksSearch(profile, contact, tcpa.mBrandID)]);
    setLeads(matchedLeads || []);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: matchedLeads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  const totalSteps = STEP_LABELS.length - firstStep;
  const currentStepNumber = step - firstStep + 1;
  const progressPercent = Math.round(currentStepNumber / totalSteps * 100);
  const selectClass = (hasError: boolean) => `block w-full pl-4 pr-10 py-3 text-sm rounded-lg border bg-white text-gray-900 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:border-navy focus:ring-navy/20'}`;
  const inputClass = (hasError: boolean) => `block w-full px-4 py-3 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:border-navy focus:ring-navy/20'}`;
  const labelClass = 'block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider';
  const primaryButton = 'w-full bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3.5 px-6 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200 inline-flex items-center justify-center gap-2';
  const backButton = 'inline-flex items-center justify-center px-4 py-3.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 transition-colors duration-200';
  const domains = domainNameFor ? Array.from(new Set(programs.map(p => domainNameFor(p) || '').filter(Boolean))) : [];
  const programOptions = domainNameFor ? domains.map(domainName => <optgroup key={domainName} label={domainName} data-id="element-342" data-mp="1jgetuvh7oyp4">
          {programs.filter(p => domainNameFor(p) === domainName).map(p => <option key={p.slug} value={p.slug} data-id="element-343" data-mp="19npli9gyk0y0">
                {p.name} ({p.credential})
              </option>)}
        </optgroup>) : programs.map(p => <option key={p.slug} value={p.slug} data-id="element-344" data-mp="zq1ofcvvqhco">
          {p.name} ({p.credential})
        </option>);
  const renderStep = () => {
    if (step === 0) {
      return <form onSubmit={handleProgramNext} className="space-y-5" data-id="element-345" data-mp="qpwi762f9p03">
          <div data-id="element-346" data-mp="qrprkt4f6nbs">
            <label htmlFor="hero-program" className={labelClass} data-id="element-347" data-mp="qrzg0c9tgmfd">
              Program of Interest
            </label>
            <div className="relative" data-id="element-348" data-mp="2l3xjdxlg2o3c">
              <select id="hero-program" value={programSlug} onChange={e => {
              setProgramSlug(e.target.value);
              if (e.target.value) setProgramError('');
            }} aria-invalid={!!programError} className={selectClass(!!programError)} data-id="element-349" data-mp="2l4787hur6p7g">
                <option value="" data-id="element-350" data-mp="37ccmx0dxlmtf">-- Choose a Program --</option>
                {programOptions}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-351" data-mp="29pmbp5xfgsic">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-352" data-mp="2ennvxu6laxve" />
              </span>
            </div>
            {programError && <p className="mt-1.5 text-xs text-red-600" data-id="element-353" data-mp="2nnu7haiq9nrx">{programError}</p>}
          </div>
          <button type="submit" className={primaryButton} data-id="element-354" data-mp="3v26zdguc99d5">
            Get Started
          </button>
        </form>;
    }
    if (step === 1) {
      return <form onSubmit={handleBackgroundNext} className="space-y-4" data-id="element-355" data-mp="2zpm0jslrevi">
          <div data-id="element-356" data-mp="1zwxua0sio3jq">
            <label htmlFor="hero-education" className={labelClass} data-id="element-357" data-mp="1zwo6mjuy28h7">
              Highest Level of Education
            </label>
            <div className="relative" data-id="element-358" data-mp="28z774xb9c90a">
              <select id="hero-education" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClass(!!errors.currentEducation)} data-id="element-359" data-mp="28xw10qucap60">
                <option value="" data-id="element-360" data-mp="1cahm4y4c30a3">-- Please Select --</option>
                {educationLevels.map(level => <option key={level} value={level} data-id="element-361" data-mp="1q1mfjih3ywbz">
                    {level}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-362" data-mp="xjiepqf8ihs3">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-363" data-mp="sjw5rt6fvnp3" />
              </span>
            </div>
            {errors.currentEducation && <p className="mt-1.5 text-xs text-red-600" data-id="element-364" data-mp="38yzg6a4ti52e">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-365" data-mp="2v8nqehvpgief">
            <label htmlFor="hero-gradyear" className={labelClass} data-id="element-366" data-mp="2v95t9xcvhuvb">
              High School Graduation Year
            </label>
            <div className="relative" data-id="element-367" data-mp="304ba4hrf4jcj">
              <select id="hero-gradyear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClass(!!errors.gradYear)} data-id="element-368" data-mp="3nw074bf6pvzt">
                <option value="" data-id="element-369" data-mp="3e0gfw2bkflhe">-- Please Select --</option>
                {gradYearOptions.map(year => <option key={year} value={year} data-id="element-370" data-mp="22ix7zwn1iuci">
                    {year}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-371" data-mp="27itb7tnwyhr0">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-372" data-mp="2mt86z2anti8k" />
              </span>
            </div>
            {errors.gradYear && <p className="mt-1.5 text-xs text-red-600" data-id="element-373" data-mp="2dz3gwl3coau3">{errors.gradYear}</p>}
          </div>

          <div data-id="element-374" data-mp="290s7uckvq4cx">
            <label htmlFor="hero-start" className={labelClass} data-id="element-375" data-mp="291bgt3sjxiet">
              When Would You Like to Start?
            </label>
            <div className="relative" data-id="element-376" data-mp="35pq3ry4d88bo">
              <select id="hero-start" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClass(!!errors.startDate)} data-id="element-377" data-mp="31p79zyiwws6u">
                <option value="" data-id="element-378" data-mp="2rq88czwkdwtc">-- Please Select --</option>
                {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-379" data-mp="1lbz6g0lhn4ax">
                    {opt}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-380" data-mp="168680yo672ac">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-381" data-mp="1b7sln97wukjo" />
              </span>
            </div>
            {errors.startDate && <p className="mt-1.5 text-xs text-red-600" data-id="element-382" data-mp="1g3zhuaai8hof">{errors.startDate}</p>}
          </div>

          <div className="flex gap-3 pt-1" data-id="element-383" data-mp="1kydesh4tewv7">
            {!hasFixedProgram && <button type="button" onClick={() => setStep(0)} className={backButton} data-id="element-384" data-mp="1p08i1r6b3kbm">
                <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-385" data-mp="1yw24c29tus6l" />
                Back
              </button>}
            <button type="submit" className={primaryButton} data-id="element-386" data-mp="23un460l639ui">
              Continue
            </button>
          </div>
        </form>;
    }
    if (step === 2) {
      return <form onSubmit={handleLocationNext} className="space-y-4" data-id="element-387" data-mp="9i6q4dm4qrf3">
          <div data-id="element-388" data-mp="1g63q8o0kzu7">
            <label htmlFor="hero-zip" className={labelClass} data-id="element-389" data-mp="1hqwbsqrq8pf">
              ZIP Code
            </label>
            <input id="hero-zip" type="text" inputMode="numeric" maxLength={5} value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} placeholder="12345" className={inputClass(!!errors.zipCode)} data-id="element-390" data-mp="3soaig4221l31" />
            {errors.zipCode && <p className="mt-1.5 text-xs text-red-600" data-id="element-391" data-mp="p5qp0y7n496n">{errors.zipCode}</p>}
            {zipLoading && <p className="mt-2 flex items-center text-xs text-gray-500" data-id="element-392" data-mp="1373291vf1wou">
                <LoaderIcon className="w-3 h-3 mr-1.5 animate-spin" aria-hidden="true" data-id="element-393" data-mp="z4zog6l9yg0e" />
                Looking up your location...
              </p>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-394" data-mp="18s2bi17ivgz1">
                <div data-id="element-395" data-mp="18rk54br5bmde">
                  <label htmlFor="hero-city" className={labelClass} data-id="element-396" data-mp="18u6ac4qkdpsg">
                    City
                  </label>
                  <input id="hero-city" type="text" value={formData.city} onChange={e => updateFormData('city', e.target.value)} placeholder="City" className={inputClass(false)} data-id="element-397" data-mp="z29133j497wd" />
                </div>
                <div data-id="element-398" data-mp="u47foby7wwhq">
                  <label htmlFor="hero-state" className={labelClass} data-id="element-399" data-mp="u3fkh28j90rz">
                    State
                  </label>
                  <input id="hero-state" type="text" value={formData.state} onChange={e => updateFormData('state', e.target.value)} placeholder="State" className={inputClass(false)} data-id="element-400" data-mp="g5r0dwmahxnz" />
                </div>
              </div>
              <div data-id="element-401" data-mp="rihxbr482l8d">
                <label htmlFor="hero-address" className={labelClass} data-id="element-402" data-mp="rlxaupi8zm7o">
                  Street Address{' '}
                  <span className="font-normal normal-case text-gray-400" data-id="element-403" data-mp="mn2hwuqkgh9u">
                    (optional)
                  </span>
                </label>
                <input id="hero-address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className={inputClass(false)} data-id="element-404" data-mp="ctvdpd7kb7bc" />
              </div>
            </>}

          <div className="flex gap-3 pt-1" data-id="element-405" data-mp="3q1q5pbr8m5hd">
            <button type="button" onClick={() => setStep(1)} className={backButton} data-id="element-406" data-mp="3q2rjvp3jv83b">
              <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-407" data-mp="2aiigo8k6p18v" />
              Back
            </button>
            <button type="submit" className={primaryButton} data-id="element-408" data-mp="2fhv4avivqcci">
              Continue
            </button>
          </div>
        </form>;
    }
    return <form onSubmit={handleSubmit} className="space-y-4" data-id="element-409" data-mp="2jidu6k17p8c2">
        <input id="leadid_token" name="leadid_token" type="hidden" data-id="element-410" data-mp="1mtpjk8rtsnco" />
        <input id="xxTrustedFormCertUrl" name="xxTrustedFormCertUrl" type="hidden" data-id="element-411" data-mp="1rs0teji4exuw" />

        <div className={wide ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4' : 'grid grid-cols-2 gap-3'} data-id="element-412" data-mp="1vtci8mt67f21">
          <div data-id="element-413" data-mp="142yaz9fnx0zc">
            <label htmlFor="hero-first" className={labelClass} data-id="element-414" data-mp="141dgtkx9fk20">
              First Name
            </label>
            <input id="hero-first" type="text" autoComplete="given-name" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="Jane" className={inputClass(!!errors.firstName)} data-id="element-415" data-mp="182p9y0fceney" />
            {errors.firstName && <p className="mt-1.5 text-xs text-red-600" data-id="element-416" data-mp="tiv3rq91gmwr">{errors.firstName}</p>}
          </div>
          <div data-id="element-417" data-mp="3sy9na8h1muuh">
            <label htmlFor="hero-last" className={labelClass} data-id="element-418" data-mp="3syrrpsdv0fdh">
              Last Name
            </label>
            <input id="hero-last" type="text" autoComplete="family-name" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={inputClass(!!errors.lastName)} data-id="element-419" data-mp="uh312qkfchi" />
            {errors.lastName && <p className="mt-1.5 text-xs text-red-600" data-id="element-420" data-mp="3e82b2ahmq828">{errors.lastName}</p>}
          </div>

        <div className={wide ? undefined : 'col-span-2'} data-id="element-421" data-mp="36pb8u8xbq7zv">
          <label htmlFor="hero-email" className={labelClass} data-id="element-422" data-mp="36mp3mfxwo4kt">
            Email Address
          </label>
          <input id="hero-email" type="email" autoComplete="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="jane@example.com" className={inputClass(!!errors.email)} data-id="element-423" data-mp="2wqvgy3qh2upb" />
          {errors.email && <p className="mt-1.5 text-xs text-red-600" data-id="element-424" data-mp="2iyfjdigcbovs">{errors.email}</p>}
        </div>

        <div className={wide ? undefined : 'col-span-2'} data-id="element-425" data-mp="2e1fdb7jhqm4o">
          <label htmlFor="hero-phone" className={labelClass} data-id="element-426" data-mp="291ayrn2581h3">
            Phone Number
          </label>
          <input id="hero-phone" type="tel" autoComplete="tel" maxLength={13} value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="(555)123-4567" className={inputClass(!!errors.phone)} data-id="element-427" data-mp="24zfxgik7po3b" />
          {errors.phone && <p className="mt-1.5 text-xs text-red-600" data-id="element-428" data-mp="1gf2rwumwmgzg">{errors.phone}</p>}
          </div>
        </div>

        <div className={wide ? 'max-w-3xl' : undefined} data-id="element-429" data-mp="1ld4azfkbygax">
        {searchLoading ? <p className="flex items-center gap-2 text-xs text-gray-500 pt-1" data-id="element-430" data-mp="293qequnaxef4">
            <LoaderIcon className="w-3.5 h-3.5 animate-spin" aria-hidden="true" data-id="element-431" data-mp="295umxos8gzl7" />
            Loading consent details...
          </p> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-id="element-432" data-mp="1zd5mrq24v856">
            <p className="text-xs font-medium text-red-800" data-id="element-433" data-mp="24c02uk8puill">
              We couldn't load the consent details.
            </p>
            <button type="button" onClick={() => {
            searchFired.current = true;
            runTcpaSearch();
          }} className="mt-2 inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-434" data-mp="2s0rrx2r0eku5">
              Retry
            </button>
          </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#1668E0]" data-id="element-435" data-mp="q52dgc1g1e1t">
            {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
            __html: sanitizeTcpaHtml(tcpa.disclosure)
          }} data-id="element-436" data-mp="bhhbilgb1kvh" /> : DEFAULT_TCPA_DISCLOSURE}
          </TCPADisclosure>}
        </div>

        {submitError && <p className="text-xs text-red-600" data-id="element-437" data-mp="1hv6wa69k1t0x">{submitError}</p>}

        <div className={`flex gap-3 pt-1 ${wide ? 'sm:max-w-md sm:ml-auto' : ''}`} data-id="element-438" data-mp="2d6ogm6p2mosc">
          <button type="button" onClick={() => setStep(2)} disabled={isSubmitting} className={backButton} data-id="element-439" data-mp="2i4q02sqesq4c">
            <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-440" data-mp="2n6p2o0ebrj08" />
            Back
          </button>
          <button type="submit" disabled={isSubmitting} className={primaryButton} data-id="element-441" data-mp="2w50qxvrpy4pi">
            {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" aria-hidden="true" data-id="element-442" data-mp="312aq434wtuqj" />}
            {isSubmitting ? 'Finding School Matches...' : 'See My Matches'}
          </button>
        </div>
      </form>;
  };
  return <div className="bg-white rounded-2xl shadow-xl overflow-hidden ring-1 ring-gray-900/5" data-id="element-443" data-mp="3ft057glhrtzo">
      <div className="bg-navy px-6 py-4" style={{
      backgroundColor: "#8FBEF5"
    }} data-id="element-444" data-mp="3fr6y606yv7r5">
        <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-445" data-mp="3jsins5pubszc">
          {heading}
        </h2>
        <p className="text-white/70 text-xs text-center mt-1" data-id="element-446" data-mp="145o8i9l67nv3">{subtext}</p>
      </div>

      <div className={wide ? 'px-6 sm:px-10 pt-5' : 'px-6 pt-5'} data-id="element-447" data-mp="1480x35lfowh4">
        <div className="flex items-center justify-between mb-1.5" data-id="element-448" data-mp="z73h8znwceis">
          <span className="text-xs font-bold uppercase tracking-wider text-navy" data-id="element-449" data-mp="u8a458ciupj5">
            Step {currentStepNumber} of {totalSteps} · {STEP_LABELS[step]}
          </span>
          <span className="text-xs text-gray-500" data-id="element-450" data-mp="1mw5vdxjbnp8j">{progressPercent}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100} aria-label="Form progress" data-id="element-451" data-mp="1myiewf4qa7np">
          <motion.div className="h-full rounded-full bg-crimson" initial={false} animate={{
          width: `${progressPercent}%`
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-452" data-mp="2anbcsh4wupky" />
        </div>
      </div>

      <div className={wide ? 'p-6 sm:p-10' : 'p-6'} data-id="element-453" data-mp="7zbe0bjfwe8p">
        <AnimatePresence mode="wait" initial={false} data-id="element-454" data-mp="815xrmiz60b7">
          {isSubmitting ?
        // Takes over the card: there is nothing left to answer, and
        // leaving editable fields on screen would invite edits that can
        // no longer reach the request.
        <motion.div key="finding" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="py-10 text-center" role="status" aria-live="polite" data-id="element-455" data-mp="3gdbo9c72l7ia">
              <LoaderIcon className="w-8 h-8 mx-auto mb-4 text-navy animate-spin" aria-hidden="true" data-id="element-456" data-mp="15mag51swiedz" />
              <p className="text-base font-bold text-navy" data-id="element-457" data-mp="1al55ynpad37l">
                Finding School Matches
              </p>
              <p className="mt-1.5 text-sm text-gray-500" data-id="element-458" data-mp="1knh7e578q2wv">
                Checking accredited schools against your answers.
              </p>
            </motion.div> : <motion.div key={step} initial={{
          opacity: 0,
          y: reduceMotion ? 0 : 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: reduceMotion ? 0 : -8
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-459" data-mp="1tjggtwa6audb">
              {renderStep()}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}